// Import shared types for consistency
import type { TestScores } from '@monorepo/types'
import type { Gender } from '@monorepo/shared-utils'
import { normalizeGender, getPronoun } from '@monorepo/shared-utils'

// Import shared learning test data from @monorepo/types (single source of truth)
import { LEARNING_TEST_DATA as learningTestData } from '@monorepo/types'

export type SectionId = keyof TestScores
export type ExtendedSectionId = SectionId | 'basic-info'

// ============================================================================
// TYPES
// ============================================================================

export interface Question {
  id: number
  text: string
  section: SectionId  // Now uses proper shared package section IDs
}

export interface SectionMeta {
  id: SectionId
  title: string
  description?: string | null
  explanation?: string | null
  questionRange: [number, number]
  phase: 1 | 2
}

export interface WizardPhase {
  id: number
  title: string
  description: string
  sections: SectionId[]
  totalQuestions: number
}

// ============================================================================
// CONSTANTS (from JSON)
// ============================================================================

/**
 * Total number of questions in the test
 * Loaded from JSON data
 */
export const TOTAL_QUESTIONS = learningTestData.metadata.totalQuestions

/**
 * Valid question number range
 * Loaded from JSON data
 */
export const QUESTION_RANGE = {
  min: learningTestData.metadata.questionRange.min,
  max: learningTestData.metadata.questionRange.max
} as const

/**
 * Validate if a question number is within valid range
 */
export function isValidQuestionNumber(questionNumber: number): boolean {
  return Number.isInteger(questionNumber) &&
         questionNumber >= QUESTION_RANGE.min &&
         questionNumber <= QUESTION_RANGE.max
}

/**
 * Get total questions count (use this instead of hardcoding)
 */
export function getTotalQuestions(): number {
  return TOTAL_QUESTIONS
}

// ============================================================================
// DATA (loaded from JSON)
// ============================================================================

/**
 * Phase definitions for split-wizard architecture
 * Loaded from JSON data
 */
export const wizardPhases: WizardPhase[] = learningTestData.wizardPhases as WizardPhase[]

/**
 * Section metadata
 * Loaded from JSON data
 */
export const sections: SectionMeta[] = learningTestData.sections as SectionMeta[]

/**
 * All test questions
 * Loaded from JSON data
 * NOTE: The texts are copied verbatim from legacy PHP for fidelity.
 */
export const questions: Question[] = learningTestData.questions as Question[]

export const getInitialAnswers = (): Record<number, number> => {
  return Object.fromEntries(questions.map(q => [q.id, 0])) as Record<number, number>
}

// ============================================================================
// PERSONALIZATION FUNCTIONS
// ============================================================================

/**
 * Personalize section metadata with child's name
 * Replaces "Ihr Kind" with the actual child name
 */
export function personalizeSection(section: SectionMeta, childName: string): SectionMeta {
  const replacePlaceholder = (text: string | null | undefined): string | null | undefined => {
    if (!text) return text
    return text.replace(/Ihr Kind/g, childName)
  }

  return {
    ...section,
    description: replacePlaceholder(section.description),
    explanation: replacePlaceholder(section.explanation),
  }
}

/**
 * Personalize a question with child's name and gender
 * Replaces:
 * - "Mein Kind" with gender-aware "Mein/Meine {childName}"
 * - "meinem Kind" with gender-aware "meinem/meiner {childName}"
 * - "es" (nominative pronoun) with gender-aware "er/sie"
 *
 * Gender rules:
 * - female: "Mein" -> "Meine", "meinem" -> "meiner", "es" -> "sie"
 * - male: "Mein" -> "Mein", "meinem" -> "meinem", "es" -> "er"
 * - other: uses "er/sie" format
 */
export function personalizeQuestion(question: Question, childName: string, gender?: Gender): Question {
  const normalizedGender = normalizeGender(gender)
  const isFemale = normalizedGender === 'female'

  // For "Mein Kind" -> "Mein/Meine {childName}" (add 'e' for female)
  const meinFormCapital = isFemale ? 'Meine' : 'Mein'
  const meinFormLower = isFemale ? 'meine' : 'mein'

  // For "meinem Kind" -> "meinem/meiner {childName}" (change to 'er' for female)
  const meinemForm = isFemale ? 'meiner' : 'meinem'

  // Get gender-aware nominative pronoun for "es" -> "er/sie"
  const pronoun = getPronoun(gender)

  let text = question.text
    .replace(/Mein Kind\b/g, `${meinFormCapital} ${childName}`)
    .replace(/mein Kind\b/g, `${meinFormLower} ${childName}`)
    .replace(/meinem Kind\b/g, `${meinemForm} ${childName}`)

  // Replace "es" (nominative pronoun) with gender-aware pronoun
  // Only replace when "es" is used as a pronoun (followed by space and a word)
  // Avoid replacing "es" in words like "Zusammenhänge"
  text = text.replace(/\bes\s+/g, `${pronoun} `)

  return {
    ...question,
    text,
  }
}

/**
 * Get personalized sections for a specific child
 */
export function getPersonalizedSections(childName: string): SectionMeta[] {
  return sections.map(section => personalizeSection(section, childName))
}

/**
 * Get personalized questions for a specific child
 */
export function getPersonalizedQuestions(childName: string, gender?: Gender): Question[] {
  return questions.map(question => personalizeQuestion(question, childName, gender))
}

/**
 * Get personalized questions for a specific section and child
 */
export function getPersonalizedSectionQuestions(sectionId: SectionId, childName: string, gender?: Gender): Question[] {
  return getPersonalizedQuestions(childName, gender).filter(q => q.section === sectionId)
}
